import { useEffect, useState } from "react";
import { KeyRound, Plug, CheckCircle2, XCircle, Save, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";

const inputCls = "w-full h-10 px-3 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";
const lbl = "text-xs font-semibold text-stone-600";

export default function AdminIntegrationTr() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  const loadStatus = () => api.get("/admin/ata-tr/sync-status").then(({ data }) => setLastSync(data.last_sync)).catch(() => {});

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setF({
      ata_tr_base_url: data.ata_tr_base_url || "https://www.ataangora.com.tr",
      ata_tr_api_key: data.ata_tr_api_key || "",
      ata_tr_api_secret: data.ata_tr_api_secret || "",
      ata_tr_merchant_id: data.ata_tr_merchant_id || "",
    })).catch((e) => toast.error(errText(e)));
    loadStatus();
  }, []);

  const save = async () => {
    setBusy("save");
    try {
      const { data: cur } = await api.get("/admin/settings");
      await api.put("/admin/settings", { ...cur, ...f });
      toast.success("Bağlantı bilgileri kaydedildi");
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const testConn = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/ata-tr/test", f);
      setTestResult(data);
      if (data.ok) toast.success(data.message); else toast.warning(data.message);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const syncNow = async () => {
    setBusy("sync");
    setSyncResult(null);
    try {
      const { data } = await api.post("/admin/ata-tr/sync", {});
      setSyncResult(data);
      toast.success(`Senkron tamamlandı — ${data.created} yeni, ${data.updated} güncellendi`);
      loadStatus();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  if (!f) return <div className="p-10 text-center text-stone-400 text-sm">Yükleniyor...</div>;

  return (
    <div data-testid="ata-tr-page" className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="font-serif text-2xl sm:text-3xl text-stone-900 flex items-center gap-2"><KeyRound className="w-6 h-6 text-forest" /> AtaAngora.com.tr API Bağlantısı</h1>
        <p className="text-sm text-stone-500 mt-1">www.ataangora.com.tr sistemiyle entegrasyon için API kimlik bilgilerini girin. Bilgiler yalnızca sunucuda saklanır.</p>
      </div>

      <section className="bg-white rounded-2xl border border-stone-200/80 p-6 space-y-4">
        <div>
          <label className={lbl}>API Adresi (Base URL)</label>
          <input data-testid="ata-tr-base-url" className={inputCls} value={f.ata_tr_base_url} onChange={(e) => setF({ ...f, ata_tr_base_url: e.target.value })} placeholder="https://www.ataangora.com.tr" />
        </div>
        <div>
          <label className={lbl}>API Key</label>
          <input data-testid="ata-tr-api-key" className={`${inputCls} font-mono`} value={f.ata_tr_api_key} onChange={(e) => setF({ ...f, ata_tr_api_key: e.target.value })} placeholder="API anahtarınız" autoComplete="off" />
        </div>
        <div>
          <label className={lbl}>API Secret</label>
          <input data-testid="ata-tr-api-secret" type="password" className={`${inputCls} font-mono`} value={f.ata_tr_api_secret} onChange={(e) => setF({ ...f, ata_tr_api_secret: e.target.value })} placeholder="API secret" autoComplete="new-password" />
        </div>
        <div>
          <label className={lbl}>Merchant ID (Mağaza No)</label>
          <input data-testid="ata-tr-merchant-id" className={`${inputCls} font-mono`} value={f.ata_tr_merchant_id} onChange={(e) => setF({ ...f, ata_tr_merchant_id: e.target.value })} placeholder="Merchant ID" autoComplete="off" />
        </div>
        <div className="flex gap-3 flex-wrap pt-2">
          <button data-testid="ata-tr-save" onClick={save} disabled={!!busy} className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-forest hover:bg-forest-dark text-white text-sm font-semibold transition-colors disabled:bg-stone-400"><Save className="w-4 h-4" /> {busy === "save" ? "Kaydediliyor..." : "Kaydet"}</button>
          <button data-testid="ata-tr-test" onClick={testConn} disabled={!!busy} className="inline-flex items-center gap-2 px-5 h-10 rounded-xl border border-stone-300 hover:border-forest text-sm font-semibold text-stone-700 transition-colors"><Plug className="w-4 h-4" /> {busy === "test" ? "Test ediliyor..." : "Bağlantıyı Test Et"}</button>
          <button data-testid="ata-tr-sync-btn" onClick={syncNow} disabled={!!busy} className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-gold hover:bg-gold-dark text-stone-900 text-sm font-semibold transition-colors disabled:bg-stone-300"><RefreshCw className="w-4 h-4" /> {busy === "sync" ? "Eşitleniyor..." : "Ürün/Stok Şimdi Eşitle"}</button>
        </div>
        {lastSync && (
          <p data-testid="ata-tr-last-sync" className="text-[11px] text-stone-400">Son eşitleme: {new Date(lastSync.at).toLocaleString("tr-TR")} — {lastSync.fetched} ürün çekildi, {lastSync.created} yeni, {lastSync.updated} güncellendi{lastSync.failed ? `, ${lastSync.failed} hatalı` : ""}</p>
        )}
        {syncResult && (
          <div data-testid="ata-tr-sync-result" className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-xl px-4 py-3">
            <p className="font-semibold">Eşitleme sonucu: {syncResult.fetched} ürün çekildi · {syncResult.created} yeni · {syncResult.updated} güncellendi · {syncResult.failed} hatalı</p>
            {syncResult.errors?.length > 0 && <p className="text-xs mt-1 text-red-600">{syncResult.errors[0]}</p>}
          </div>
        )}
        {testResult && (
          <div data-testid="ata-tr-test-result" className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm border ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            {testResult.message}
          </div>
        )}
      </section>
    </div>
  );
}
